import { useState } from 'react';
import useSWR from 'swr';
import { Users, UserPlus, Pencil, Trash2, ShieldCheck, AlertCircle, Eye, EyeOff } from 'lucide-react';
import apiClient from '@/lib/api';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type UserRol = 'admin' | 'user' | 'employee';

interface UserRow {
  id: string;
  nombre: string;
  email: string;
  rol: UserRol;
  activo: boolean;
  employee_id: string | null;
  employee_nombre: string | null;
  employee_apellidos: string | null;
  created_at: string;
}

interface EmployeeOption {
  id: string;
  nombre: string;
  apellidos: string;
  email: string;
}

interface UserFormState {
  nombre: string;
  email: string;
  password: string;
  rol: UserRol;
  employee_id: string;
  activo: boolean;
}

// ─── Helpers visuales ─────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '0.875rem',
  fontFamily: 'system-ui, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-muted)', fontFamily: 'system-ui, sans-serif' }}>
        {label}{required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function RolBadge({ rol }: { rol: UserRol }) {
  const map: Record<UserRol, { label: string; bg: string; color: string }> = {
    admin:    { label: 'Admin',    bg: 'color-mix(in srgb, var(--color-primary) 12%, transparent)', color: 'var(--color-primary)' },
    user:     { label: 'Usuario',  bg: 'color-mix(in srgb, var(--color-muted) 15%, transparent)',   color: 'var(--color-muted)' },
    employee: { label: 'Empleado', bg: 'color-mix(in srgb, var(--color-accent) 12%, transparent)',  color: 'var(--color-accent)' },
  };
  const s = map[rol] ?? map.user;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

// ─── Validación de contraseña ─────────────────────────────────────────────────

interface PwdStrength { score: number; label: string; color: string }

function getPasswordStrength(pwd: string): PwdStrength {
  if (!pwd) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score, label: 'Muy débil', color: '#ef4444' };
  if (score === 2) return { score, label: 'Débil',    color: '#f97316' };
  if (score === 3) return { score, label: 'Media',    color: '#eab308' };
  if (score === 4) return { score, label: 'Fuerte',   color: '#22c55e' };
  return { score, label: 'Muy fuerte', color: '#10b981' };
}

// ─── Modal Crear / Editar ─────────────────────────────────────────────────────

function UserModal({
  user,
  employees,
  onClose,
  onSaved,
}: {
  user: UserRow | null;
  employees: EmployeeOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!user;
  const [form, setForm] = useState<UserFormState>({
    nombre:      user?.nombre ?? '',
    email:       user?.email  ?? '',
    password:    '',
    rol:         user?.rol    ?? 'user',
    employee_id: user?.employee_id ?? '',
    activo:      user?.activo ?? true,
  });
  const [saving, setSaving]     = useState(false);
  const [error,  setError]      = useState<string | null>(null);
  const [showPwd, setShowPwd]   = useState(false);

  const pwdStrength = getPasswordStrength(form.password);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isEdit && form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        nombre:      form.nombre,
        email:       form.email,
        rol:         form.rol,
        activo:      form.activo,
        employee_id: form.employee_id || null,
      };
      if (form.password) payload.password = form.password;

      if (isEdit) {
        await apiClient.put(`/users/${user!.id}`, payload);
      } else {
        await apiClient.post('/users', payload);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al guardar el usuario.');
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', fontFamily: 'system-ui, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)' }}>
            {isEdit ? `Editar usuario — ${user!.nombre}` : 'Nuevo usuario'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)', borderRadius: 6, padding: '0.75rem', marginBottom: 16, fontSize: '0.8125rem', color: 'var(--color-danger)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Nombre completo" required>
                <input name="nombre" value={form.nombre} onChange={handleChange} required style={inputStyle} placeholder="Juan García" />
              </Field>
              <Field label="Email" required>
                <input name="email" type="email" value={form.email} onChange={handleChange} required style={inputStyle} placeholder="juan@empresa.com" />
              </Field>
            </div>

            {/* Contraseña con indicador de fortaleza */}
            <Field label={isEdit ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'} required={!isEdit}>
              <div style={{ position: 'relative' }}>
                <input
                  name="password"
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required={!isEdit}
                  style={{ ...inputStyle, paddingRight: '2.5rem' }}
                  placeholder={isEdit ? '••••••••' : 'Mínimo 8 caracteres'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', padding: 0, display: 'flex' }}
                >
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {form.password && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= pwdStrength.score ? pwdStrength.color : 'var(--color-border)', transition: 'background 0.2s' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: pwdStrength.color, fontWeight: 500 }}>{pwdStrength.label}</span>
                </div>
              )}
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Rol" required>
                <select name="rol" value={form.rol} onChange={handleChange} style={inputStyle}>
                  <option value="user">Usuario</option>
                  <option value="admin">Admin</option>
                  <option value="employee">Empleado</option>
                </select>
              </Field>

              <Field label="Estado">
                <select name="activo" value={form.activo ? '1' : '0'} onChange={e => setForm(prev => ({ ...prev, activo: e.target.value === '1' }))} style={inputStyle}>
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </Field>
            </div>

            {/* Selector de empleado — solo visible si rol es 'employee' */}
            {form.rol === 'employee' && (
              <Field label="Empleado vinculado">
                <select name="employee_id" value={form.employee_id} onChange={handleChange} style={inputStyle}>
                  <option value="">— Sin vincular —</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.apellidos}, {emp.nombre} ({emp.email})
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: 2 }}>
                  El empleado podrá acceder a sus nominas con este usuario.
                </span>
              </Field>
            )}

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <button type="button" onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'system-ui, sans-serif' }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500, fontFamily: 'system-ui, sans-serif' }}>
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Confirmar eliminación ───────────────────────────────────────────────

function DeleteModal({ user, onClose, onConfirmed }: { user: UserRow; onClose: () => void; onConfirmed: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  async function handleDelete() {
    setLoading(true);
    try {
      await apiClient.delete(`/users/${user.id}`);
      onConfirmed();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al desactivar el usuario.');
      setLoading(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 400, fontFamily: 'system-ui, sans-serif' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)' }}>Desactivar usuario</h2>
        <p style={{ margin: '0 0 20px', fontSize: '0.875rem', color: 'var(--color-muted)', lineHeight: 1.5 }}>
          El usuario <strong style={{ color: 'var(--color-text)' }}>{user.nombre}</strong> ({user.email}) quedará inactivo y no podrá iniciar sesión. Esta acción es reversible desde el panel de edición.
        </p>
        {error && (
          <div style={{ background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)', borderRadius: 6, padding: '0.625rem', marginBottom: 16, fontSize: '0.8125rem', color: 'var(--color-danger)' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'system-ui, sans-serif' }}>Cancelar</button>
          <button onClick={handleDelete} disabled={loading} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-danger)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500, fontFamily: 'system-ui, sans-serif' }}>
            {loading ? 'Desactivando...' : 'Desactivar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function UsersPage() {
  const { data, mutate, isLoading } = useSWR<{ data: UserRow[] }>(
    '/users',
    (url: string) => apiClient.get(url).then(r => r.data),
  );

  const { data: empData } = useSWR<{ data: EmployeeOption[] }>(
    '/rrhh/employees?limit=200',
    (url: string) => apiClient.get(url).then(r => r.data),
  );

  const users     = data?.data     ?? [];
  const employees = empData?.data  ?? [];

  const [modalUser,  setModalUser]  = useState<UserRow | null | 'new'>(null);
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', fontFamily: 'system-ui, sans-serif' }}>

      {/* Modales */}
      {modalUser !== null && (
        <UserModal
          user={modalUser === 'new' ? null : modalUser}
          employees={employees}
          onClose={() => setModalUser(null)}
          onSaved={() => mutate()}
        />
      )}
      {deleteUser !== null && (
        <DeleteModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onConfirmed={() => mutate()}
        />
      )}

      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={20} style={{ color: 'var(--color-primary)' }} />
            Usuarios
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
            Gestión de accesos al sistema para este tenant.
          </p>
        </div>
        <button
          onClick={() => setModalUser('new')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.5rem 1.125rem', borderRadius: 7, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, fontFamily: 'system-ui, sans-serif' }}
        >
          <UserPlus size={15} /> Nuevo usuario
        </button>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)', fontSize: '0.875rem' }}>Cargando usuarios...</div>
        ) : users.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-muted)' }}>
            <ShieldCheck size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>No hay usuarios creados aún.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-table-header)' }}>
                {['Usuario', 'Rol', 'Empleado vinculado', 'Estado', 'Creado', ''].map(h => (
                  <th key={h} style={{ padding: '0.625rem 1rem', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--color-border)' : 'none', transition: 'background 0.1s' }}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)' }}>{u.nombre}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>{u.email}</div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <RolBadge rol={u.rol} />
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
                    {u.employee_nombre
                      ? <span>{u.employee_apellidos}, {u.employee_nombre}</span>
                      : <span style={{ opacity: 0.45 }}>—</span>
                    }
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                      background: u.activo ? 'rgba(16,185,129,0.12)' : 'rgba(100,116,139,0.12)',
                      color:      u.activo ? '#10b981'               : '#94a3b8',
                    }}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.75rem', color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(u.created_at).toLocaleDateString('es-ES')}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setModalUser(u)}
                        title="Editar usuario"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-muted)', cursor: 'pointer' }}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => setDeleteUser(u)}
                        title="Desactivar usuario"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 6, border: '1px solid color-mix(in srgb, var(--color-danger) 35%, transparent)', background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)', color: 'var(--color-danger)', cursor: 'pointer' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
